/**
 * Boat.ts — vessel state + simple hull hydrodynamics.
 *
 * State: 2D position on water plane (world XZ), surge velocity, sway velocity,
 * heading (yaw), yaw rate; vertical heave + pitch + roll integrated by
 * buoyancy (#30).
 *
 * Hydrodynamics:
 *   - Throttle drives surge force
 *   - Steering torque (small at low speed, larger at high speed) drives yaw
 *   - Linear + quadratic drag on each axis; lateral drag much higher than
 *     longitudinal (so the boat turns instead of side-slipping).
 *   - Wind drag on the superstructure (Blendermann 1994, C_D≈0.8) added so
 *     a moored boat drifts visibly in U=10 m/s.
 *   - Buoyancy: 6 hull sample points, per-point Archimedes restoring force
 *     against a CPU `WaterField` proxy of the rendered surface.
 */
import type { WaterField } from "./WaterField";
import type { BoatHull } from "./BoatHull";
import { ANTILA_33_3, defaultKeelSamplePoints } from "./BoatHull";

/** @deprecated Use BoatHull from BoatHull.ts. Kept as alias for compatibility. */
export type BoatParams = BoatHull;

const RHO_AIR = 1.225;

export const RUDDER_MAX_ANGLE_RAD = (45 * Math.PI) / 180;
export const RUDDER_RATE_RAD_PER_SEC = (60 * Math.PI) / 180;

export class Boat {
  /** World-space XZ position. */
  posX = 0;
  posZ = 0;
  /** World-space Y of hull centreline (set from buoyancy). */
  posY: number;
  /** Vertical (heave) velocity. */
  vy = 0;
  /** Body-frame velocities. */
  surge = 0; // forward (+X_body)
  sway = 0; // sideways (+Z_body)
  /** Yaw angle (rad). 0 = facing world -Z; +ve rotates toward +X. */
  heading = 0;
  yawRate = 0;
  /** Pitch about body +Z (right) axis. +ve raises bow. */
  pitch = 0;
  pitchRate = 0;
  /** Roll about body +X (forward) axis. +ve dips starboard. */
  roll = 0;
  rollRate = 0;

  /** Inputs in [-1, 1]. */
  throttle = 0;
  steer = 0;
  /**
   * Engine gas grip in [0, 1]. Independent from {@link gear}. Maintained
   * by the input layer so a UI / keyboard split (grip vs. F/N/R lever)
   * matches a real outboard: turning the lever to N keeps the grip
   * position, and dropping the grip never re-engages a gear.
   * The combined drive force still comes from `throttle = gear * gas`,
   * written each frame.
   */
  gas = 0;
  /**
   * Engine gear: -1 = reverse, 0 = neutral, +1 = forward. Stays where the
   * user (or AI) puts it — touching the gas does not change it.
   */
  gear: -1 | 0 | 1 = 0;
  /** Bow thruster lateral input in [-1, 1]. + = push bow to starboard. */
  bowThrust = 0;

  /**
   * Actual rudder deflection (rad), driven by `steer` through a rate
   * limiter so the helm has finite slew speed (Etap 5B). Positive value
   * = rudder trailing edge to starboard → bow yaws to starboard in
   * forward motion.
   */
  rudderAngle = 0;

  /** Independent outboard steering command in [-1, 1]. */
  engineSteer = 0;
  /** Actual independently-steered outboard angle (rad). */
  engineAngle = 0;

  /**
   * Last rudder side-force magnitude (N). Read-only; updated each step.
   * Useful for HUD diagnostics.
   */
  rudderForce = 0;

  /**
   * Centerboard extension in [0, 1]. 1 = fully down (max anti-drift).
   * Ignored for hulls whose `lateral.kind` is not "centerboard".
   */
  centerboardExtension = 1;

  /** Visual anchor deployment in [0, 1]. Drives GLB part animation. */
  anchorDeployT = 0;

  /**
   * Mast rotation (rad) for foldable-mast yachts (tabernacle, e.g.
   * Antila 27). 0 = mast stepped vertically; π/2 = mast lying aft on
   * the gallows. Driven by the crew "Maszt" panel through
   * {@link mastAngleTarget}; the value is slewed toward the target
   * each `step` so the change is visible (a few seconds). Ignored
   * for hulls without `params.foldableMast`.
   */
  mastAngle = 0;
  /** Desired mast angle (rad). Crew commands set this; `step` ramps. */
  mastAngleTarget = 0;
  /** Accumulated time (s) used to wobble the mast slew rate. */
  private _mastPhase = 0;

  /**
   * True after the crew has detached the boom from the gooseneck
   * (required before lowering the mast on a tabernacle). Renderer
   * hides any scene part whose name starts with `boom` while this
   * flag is set. Re-cleared when the boom is re-attached after the
   * mast is raised. Only meaningful for `foldableMast` hulls.
   */
  boomDetached = false;

  /**
   * Last leeway angle (rad) computed at the lateral resistance centre.
   * Read-only; updated each `step`. Useful for HUD / debug overlay.
   */
  leeway = 0;

  /** Last lift force magnitude (N) from the lateral resistance surface. */
  lateralLift = 0;

  /**
   * Accumulated external force (world frame, N) applied during the next
   * `step` and then cleared. Used by mooring lines, fenders, etc.
   */
  private extForceWX = 0;
  private extForceWZ = 0;
  /**
   * Accumulated external yaw torque from forces applied at off-CG points
   * (computed in body frame as r × F).
   */
  private extYawTorque = 0;

  /**
   * Apply an external force `(fwx, fwz)` (world frame, N) at the world
   * point `(pwx, pwz)`. Accumulated until the next `step` consumes it.
   * The torque is computed in body frame from the cleat offset, so the
   * force naturally produces both translation and rotation.
   */
  applyExternalForce(fwx: number, fwz: number, pwx: number, pwz: number): void {
    this.extForceWX += fwx;
    this.extForceWZ += fwz;
    // Convert offset to body frame to compute yaw torque (XZ plane).
    const dx = pwx - this.posX;
    const dz = pwz - this.posZ;
    const ch = Math.cos(this.heading);
    const sh = Math.sin(this.heading);
    // body axes: fwd = (sh, -ch), rgt = (ch, sh)
    const ox = dx * sh + dz * -ch; // body forward component of arm
    const oz = dx * ch + dz * sh; // body starboard component of arm
    // body force components
    const fb_fwd = fwx * sh + fwz * -ch;
    const fb_rgt = fwx * ch + fwz * sh;
    // yaw torque: r × F  (positive = bow toward starboard)
    this.extYawTorque += ox * fb_rgt - oz * fb_fwd;
  }

  /**
   * World-frame position of a body-local point. Useful for mooring cleats
   * (anchored at body coordinates and consumed in world frame each step).
   */
  bodyToWorld(bodyX: number, _bodyY: number, bodyZ: number): [number, number] {
    const ch = Math.cos(this.heading);
    const sh = Math.sin(this.heading);
    const fwdX = sh,
      fwdZ = -ch;
    const rgtX = ch,
      rgtZ = sh;
    return [
      this.posX + bodyX * fwdX + bodyZ * rgtX,
      this.posZ + bodyX * fwdZ + bodyZ * rgtZ,
    ];
  }

  /**
   * World-frame velocity of a body-local point (XZ plane only). Includes
   * the contribution of yaw rate.
   */
  velocityAtBodyPoint(bodyX: number, bodyZ: number): [number, number] {
    const ch = Math.cos(this.heading);
    const sh = Math.sin(this.heading);
    // Body-frame velocity at point: surge_at = surge - yaw*0,
    // sway_at = sway + yaw*bodyX. (No yaw‧bodyZ term since +X forward.)
    const surgeAt = this.surge - this.yawRate * bodyZ;
    const swayAt = this.sway + this.yawRate * bodyX;
    // Project body velocity into world frame.
    const fwdX = sh,
      fwdZ = -ch;
    const rgtX = ch,
      rgtZ = sh;
    const wx = surgeAt * fwdX + swayAt * rgtX;
    const wz = surgeAt * fwdZ + swayAt * rgtZ;
    return [wx, wz];
  }

  constructor(public readonly params: BoatHull = ANTILA_33_3) {
    // Equilibrium hull centreline = keelDepth - draftEq above water.
    this.posY = params.keelDepth - params.draftEq;
  }

  /**
   * Integrate one physics step. Wind in world frame, m/s.
   * Buoyancy is computed against the supplied WaterField (CPU proxy of the
   * rendered FFT surface). Pass `waterLevel` (m) and current sim time `t`.
   */
  step(
    dt: number,
    windX: number,
    windZ: number,
    water: WaterField,
    waterLevel: number,
  ): void {
    const p = this.params;

    // Body-frame basis. heading=0 → forward = (0, 0, -1)? We choose:
    //   forwardWorld = ( sin(h), 0, -cos(h) )
    //   rightWorld   = ( cos(h), 0,  sin(h) )
    const ch = Math.cos(this.heading);
    const sh = Math.sin(this.heading);
    const fwdX = sh;
    const fwdZ = -ch;
    const rgtX = ch;
    const rgtZ = sh;

    {
      const target = this.steer * RUDDER_MAX_ANGLE_RAD;
      const slew = RUDDER_RATE_RAD_PER_SEC * dt;
      const dRud = Math.max(-slew, Math.min(slew, target - this.rudderAngle));
      this.rudderAngle += dRud;

      if (p.engineType === "outboard" && !p.engineSteeringCoupled) {
        const engineTarget = this.engineSteer * RUDDER_MAX_ANGLE_RAD;
        const dEngine = Math.max(
          -slew,
          Math.min(slew, engineTarget - this.engineAngle),
        );
        this.engineAngle += dEngine;
      } else if (p.engineType === "outboard") {
        this.engineSteer = this.steer;
        this.engineAngle = this.rudderAngle;
      } else {
        this.engineSteer = 0;
        this.engineAngle = 0;
      }
    }

    if (p.foldableMast) {
      // Dwie osoby luzują wanty/sztagi nierównomiernie, więc maszt
      // schodzi szarpnięciami — sinusoidalny modulator (0..1) chwilowo
      // zeruje prędkość, narzucony na średnią ≈ 0.17 rad/s. Razem π/2
      // schodzi w ~9 s (2× wolniej niż czysty slew), co odpowiada
      // realnemu tempu kładzenia masztu w pojedynkę / we dwoje.
      const MAST_BASE_RAD_PER_SEC = 0.17;
      this._mastPhase += dt;
      const wobble =
        0.25 +
        0.55 * (0.5 + 0.5 * Math.sin(this._mastPhase * 2.4)) +
        0.2 * (0.5 + 0.5 * Math.sin(this._mastPhase * 0.9 + 1.3));
      const slew = MAST_BASE_RAD_PER_SEC * wobble * dt;
      const dM = Math.max(
        -slew,
        Math.min(slew, this.mastAngleTarget - this.mastAngle),
      );
      this.mastAngle += dM;
    }

    // Throttle force magnitude. Reverse is weaker than forward.
    const thrust =
      this.throttle >= 0
        ? this.throttle * p.thrustMax
        : this.throttle * p.thrustMax * p.thrustReverseFrac;

    // -------------------------------------------------------------------
    // Engine geometry — body-frame thrust vector + propeller position.
    //
    // Outboard with steering coupling: the whole motor swings with the
    // helm, so the thrust line rotates by `rudderAngle`. At zero forward
    // speed this is what generates the famous "pivot turn" — the rudder
    // lift model alone (which scales with v²) cannot do that.
    //
    // Inboard: thrust is fixed along the hull centreline. Manoeuvring at
    // low speed relies on the prop-wash jet across the rudder (modeled
    // separately in the rudder block below).
    //
    // In reverse, an inboard with a single right-hand prop typically
    // "walks" the stern to port. We add a constant lateral force scaled
    // by `propWalkFrac` whenever throttle < 0.
    // -------------------------------------------------------------------
    let engineFx: number; // body forward
    let engineFz: number; // body starboard
    if (p.engineType === "outboard") {
      const thrustAngle = p.engineSteeringCoupled
        ? this.rudderAngle
        : this.engineAngle;
      const ca = Math.cos(thrustAngle);
      const sa = Math.sin(thrustAngle);
      engineFx = thrust * ca;
      const pivotSpeed = Math.max(0.5, p.spec.cruiseSpeed * 0.6);
      const speedRatio = Math.abs(this.surge) / pivotSpeed;
      const vectorAuthority = p.engineSteeringCoupled
        ? 1 / (1 + speedRatio * speedRatio)
        : 1;
      // Sign convention: positive helm (rudderAngle > 0) → bow turns to
      // starboard. Stern is at engineOffsetX < 0, so we need a body-+Z
      // thrust component for positive rudder. The kinematic linkage on
      // boats like the Antila is exactly this: tiller right ⇒ outboard
      // tail swings to port ⇒ thrust direction tilts to starboard.
      engineFz = thrust * sa * vectorAuthority;
    } else {
      engineFx = thrust;
      engineFz = 0;
    }
    // Prop walk in reverse only.
    if (this.throttle < 0 && p.propWalkFrac > 0) {
      // Stern to port → reaction force on hull is to port (body -Z).
      engineFz -= p.propWalkFrac * Math.abs(this.throttle) * p.thrustMax;
    }
    // Yaw torque from off-CG thrust (pure surge contributes 0 since the
    // engine sits on the centreline; sway component generates lever arm).
    const engineYaw = -p.engineOffsetX * engineFz;

    // Bow thruster: lateral force at the bow. Produces sway force AND a yaw
    // torque about CG (lever arm ≈ LOA/2).
    const bowF = this.bowThrust * p.bowThrusterForce;
    const bowLever = p.length * 0.5;

    // -------------------------------------------------------------------
    // Rudder — lift-based steering (Etap 5B).
    //
    // The rudder is a lifting surface at the stern. Its angle of attack
    // relative to the local water flow generates a side force (lift) which
    // produces a yaw torque about the CG via the rudder's X offset. Two
    // important properties fall out of this model that the legacy `steerK`
    // shortcut could not capture:
    //   1. force scales with v² (ineffective at low speed),
    //   2. force flips sign in reverse (boats steer the "wrong way" backing
    //      out without prop-wash help).
    //
    // We rate-limit the helm so positive `steer` ramps `rudderAngle` toward
    // ±RUD_MAX over RUD_RATE rad/s.
    // -------------------------------------------------------------------
    let rudderSurge = 0;
    let rudderSway = 0;
    let rudderYaw = 0;
    {
      const r = p.rudder;
      const rudArea = r.area;
      const ox = r.offsetX;
      // Velocity of water past the rudder (body frame). Yaw rate adds a
      // lateral component at the rudder station: a point at +X has
      // tangential velocity (0, +yaw·x).
      //
      // Damping factor on the yaw-rate contribution to the local rudder
      // flow. Pure kinematics says this should be 1.0, but the rudder
      // sits in a turbulent wake where the cross-flow component is
      // partially smeared out. Lowering this damps the runaway turn
      // behaviour that tiny helm inputs otherwise trigger.
      const YAW_INDUCED_VSIDE_FRAC = 0.6;
      const vfwd = this.surge;
      const vside = this.sway + this.yawRate * ox * YAW_INDUCED_VSIDE_FRAC;
      // Apply a wake fraction — the rudder sits in the hull's wake and
      // sees a reduced fraction of free-stream surge. Lower values curb
      // the yaw-rate positive-feedback loop (vside_at_rudder = yawRate·ox
      // grows with rotation, raising effective AoA on the rudder and
      // amplifying the turn). Empirically 0.45 keeps the boat directionally
      // stable on a small helm without killing low-speed responsiveness.
      const WAKE_FRAC = 0.45;
      // Propeller wash: when the prop pushes close to the stern centreline,
      // it accelerates a column of water around the rudder and stern. This
      // is strongest for inboards whose propeller is directly ahead of the
      // rudder, but a fixed/uncoupled outboard still gives the rudder some
      // low-speed authority through the stern wash and rapid hull slipstream.
      // We model the induced jet velocity by momentum theory:
      //   v_jet = sqrt(2·T / (ρ·A_disc))
      // The prop column covers only a fraction of the rudder area, so we
      // blend free-stream flow with wash-augmented flow weighted by
      // PROP_WASH_FRAC. Without this, a stationary boat at idle throttle
      // generates unrealistically strong yaw torque.
      //
      // Coupled outboards already get their manoeuvring force from the
      // rotated thrust vector above. For an uncoupled outboard, keep only a
      // weaker wash term, fading out as the engine is steered away from the
      // rudder's centreline.
      const RHO_W = 1000;
      const PROP_DISC_AREA = 0.25;
      const INBOARD_PROP_WASH_FRAC = 0.35;
      const UNCOUPLED_OUTBOARD_WASH_FRAC = 0.24;
      // In reverse, the prop sucks water from astern and pumps it
      // forward — the rudder sits in this reversed jet. The induced
      // flow is weaker than the forward-thrust case (rudder blade is
      // outside the contracted slipstream) but not zero. Without this,
      // an inboard reversing barely turns until significant stern way
      // builds up. Empirical fraction ~30 % of forward-wash speed.
      const REVERSE_WASH_FRAC = 0.3;
      let propWash = 0;
      let propWashFrac = 0;
      if (p.engineType === "inboard") {
        propWashFrac = INBOARD_PROP_WASH_FRAC;
        if (thrust > 0) {
          propWash = Math.sqrt((2 * thrust) / (RHO_W * PROP_DISC_AREA));
        } else if (thrust < 0) {
          // Negative sign: jet flows astern→forward across rudder.
          propWash =
            -REVERSE_WASH_FRAC *
            Math.sqrt((2 * Math.abs(thrust)) / (RHO_W * PROP_DISC_AREA));
        }
      } else if (!p.engineSteeringCoupled) {
        const engineAlignment = Math.max(0, Math.cos(this.engineAngle));
        propWashFrac = UNCOUPLED_OUTBOARD_WASH_FRAC * engineAlignment;
        if (thrust > 0) {
          propWash = Math.sqrt((2 * thrust) / (RHO_W * PROP_DISC_AREA));
        } else if (thrust < 0) {
          propWash =
            -REVERSE_WASH_FRAC *
            Math.sqrt((2 * Math.abs(thrust)) / (RHO_W * PROP_DISC_AREA));
        }
      }
      const vfwdAtRud = vfwd * WAKE_FRAC + propWash * propWashFrac;
      const flowMag = Math.hypot(vfwdAtRud, vside);
      const fwdSign = vfwdAtRud >= 0 ? 1 : -1;
      if (rudArea > 1e-4 && flowMag > 0.05) {
        // Local leeway angle of water past the rudder, computed in the
        // "forward-flow" frame so reverse motion can be handled by an
        // explicit sign flip rather than by wrapping α across the stall.
        const absVfwd = Math.abs(vfwdAtRud);
        const leeway = Math.atan2(vside, Math.max(absVfwd, 1e-3));
        const alpha = this.rudderAngle - leeway;
        // C_L curve: linear thin-airfoil up to STALL, then Viterna-style
        // post-stall (flat-plate ~sin(2α)). Past stall a real foil keeps
        // generating side force with sin(2α) shape — important when the
        // boat skids sideways (heavy leeway under beam wind), where the
        // earlier Gaussian decay collapsed CL to 0 and made the rudder
        // useless even at full helm.
        const STALL = (25 * Math.PI) / 180;
        const CL_ALPHA = 3.0;
        const aAbs = Math.abs(alpha);
        const sgnA = Math.sign(alpha);
        let CL: number;
        if (aAbs < STALL) {
          CL = CL_ALPHA * Math.sin(alpha);
        } else {
          // Match value at stall, then continue as sin(2α) out to 90°.
          const CL_stall = CL_ALPHA * Math.sin(STALL);
          const k = CL_stall / Math.sin(2 * STALL);
          CL = sgnA * k * Math.sin(2 * aAbs);
        }
        const RHO = 1000;
        const q = 0.5 * RHO * flowMag * flowMag;
        // Side force on the rudder in body-frame +Z direction. The
        // +fwdSign convention here gives positive yaw torque for positive
        // helm in forward motion (matching the legacy steerK behaviour),
        // and naturally flips the sign in reverse so a right-helm boat
        // backs to port — like a real rudder without prop wash.
        const Fside = fwdSign * q * rudArea * CL;
        // Induced + parasite drag along the local flow direction. Past
        // stall add a flat-plate normal-drag term ~2·sin²(α) so the
        // rudder also contributes to slowing skid (and the boat).
        const CDi =
          aAbs < STALL
            ? 0.05 + 0.3 * CL * CL
            : 0.05 + 2.0 * Math.sin(aAbs) * Math.sin(aAbs);
        const Fdrag = -fwdSign * q * rudArea * CDi;
        rudderSway = Fside;
        rudderSurge = Fdrag;
        // Yaw torque: stern is at ox<0, so a stern side force in +Z
        // produces a CW (right) turn from above. Heading convention is
        // h+= yawRate*dt with fwd=(sin h, -cos h), i.e. +yaw = CW from
        // above. Hence +helm (steer right) must give +yaw → flip ox sign.
        rudderYaw = -ox * Fside;
        this.rudderForce = Math.abs(Fside);
      } else {
        this.rudderForce = 0;
      }
    }

    // Wind apparent velocity in world frame: (wind − boatWorldVel).
    const worldVx = this.surge * fwdX + this.sway * rgtX;
    const worldVz = this.surge * fwdZ + this.sway * rgtZ;
    const appX = windX - worldVx;
    const appZ = windZ - worldVz;
    const appMag = Math.hypot(appX, appZ);
    // Aerodynamic drag scales with V^2; using V made windage ~order(s)
    // too small and boats barely drifted.
    const windForceMag =
      0.5 * RHO_AIR * p.windCD * p.windArea * appMag * appMag;
    const windFX = appMag > 1e-4 ? windForceMag * (appX / appMag) : 0;
    const windFZ = appMag > 1e-4 ? windForceMag * (appZ / appMag) : 0;
    // Project wind force into body frame (surge / sway components).
    const windSurge = windFX * fwdX + windFZ * fwdZ;
    const windSway = windFX * rgtX + windFZ * rgtZ;
    // Yaw torque from wind side-force acting at aerodynamic centre offset.
    // T_wind = x_ce * F_sway  (with +X forward). Negative x_ce (aft CE)
    // yields weathercock tendency: bow turns into the wind.
    const windYawTorque = p.windYawLever * windSway;

    // Hull drag (body frame).
    const dragSurge =
      -Math.sign(this.surge) *
      (p.dragLinSurge * Math.abs(this.surge) +
        p.dragQuadSurge * this.surge * this.surge);
    const dragSway =
      -Math.sign(this.sway) *
      (p.dragLinSway * Math.abs(this.sway) +
        p.dragQuadSway * this.sway * this.sway);

    // -------------------------------------------------------------------
    // Lateral resistance (anti-drift) — lift on keel / centerboard.
    // Force is applied at the lateral pressure centre, so it produces
    // both a side force and a yaw torque (r × F). For "none" effective
    // area is 0 and the whole block contributes nothing.
    //
    // Body-frame velocity at the lateral surface includes a contribution
    // from yaw rate: v_at = (surge - yaw·oz, sway + yaw·ox).
    // -------------------------------------------------------------------
    const latKind = p.lateral.kind;
    const latExt =
      latKind === "centerboard"
        ? Math.max(0, Math.min(1, this.centerboardExtension))
        : latKind === "keel"
          ? 1
          : 0;
    let liftSurge = 0;
    let liftSway = 0;
    let liftYaw = 0;
    // -------------------------------------------------------------------
    // Two paths:
    //  • hull.lateralPanels present → loop over distributed panels
    //    (preferred; gives natural yaw damping + side-force levers).
    //  • else fall back to the legacy single foil in hull.lateral.
    // -------------------------------------------------------------------
    const panels = p.lateralPanels;
    if (panels && panels.length > 0) {
      let totalArea = 0;
      let totalLift = 0;
      let leewayAccumNum = 0;
      let leewayAccumDen = 0;
      const RHO = 1000;
      const STALL = (12 * Math.PI) / 180;
      const CL_ALPHA = 3.0;
      for (const panel of panels) {
        const ext = panel.coupledToBoard ? latExt : 1;
        const A = panel.area * ext;
        if (A < 1e-5) continue;
        const ox = panel.offsetX;
        const vfwd = this.surge;
        const vside = this.sway + this.yawRate * ox;
        const vmag = Math.hypot(vfwd, vside);
        if (vmag < 0.05) continue;
        const alpha = Math.atan2(vside, vfwd);
        const alphaAbs = Math.abs(alpha);
        const sgn = Math.sign(alpha);
        let CL: number;
        if (alphaAbs < STALL) {
          CL = CL_ALPHA * Math.sin(alpha);
        } else {
          const CL_stall = CL_ALPHA * Math.sin(STALL);
          const k = CL_stall / Math.sin(2 * STALL);
          CL = sgn * k * Math.sin(2 * alphaAbs);
        }
        const q = 0.5 * RHO * vmag * vmag;
        const Lmag = q * A * CL;
        // Hydrodynamic lift must oppose side-slip: for positive leeway
        // (vside > 0) the side force must point to port (body -Z).
        const fX = vside / vmag;
        const fZ = -vfwd / vmag;
        const lsx = Lmag * fX;
        const lsy = Lmag * fZ;
        const CDi =
          alphaAbs < STALL
            ? 0.05 + 0.5 * CL * CL
            : 0.05 + 2.0 * Math.sin(alphaAbs) * Math.sin(alphaAbs);
        const Dmag = q * A * CDi;
        const dsx = -Dmag * (vfwd / vmag);
        const dsy = -Dmag * (vside / vmag);
        liftSurge += lsx + dsx;
        liftSway += lsy + dsy;
        liftYaw += ox * (lsy + dsy);
        totalArea += A;
        totalLift += Math.abs(Lmag);
        leewayAccumNum += alpha * A;
        leewayAccumDen += A;
      }
      this.leeway = leewayAccumDen > 0 ? leewayAccumNum / leewayAccumDen : 0;
      this.lateralLift = totalLift;
    } else {
      const latArea = p.lateral.area * latExt;
      if (latArea > 1e-4) {
        const ox = p.lateral.offsetX;
        // No lateral offset oz — keel sits on the centreline.
        const vfwd = this.surge - this.yawRate * 0;
        const vside = this.sway + this.yawRate * ox;
        const vmag = Math.hypot(vfwd, vside);
        if (vmag > 0.05) {
          // Local angle of attack at the foil (a.k.a. local leeway).
          const alpha = Math.atan2(vside, vfwd);
          const alphaAbs = Math.abs(alpha);
          const sgn = Math.sign(alpha);
          // Linear lift to ~12°, then Viterna-style flat-plate post-stall
          // (~sin(2α)). Earlier Gaussian decay collapsed lift to 0 at deep
          // leeway, letting the boat slide unchecked under beam wind. The
          // flat-plate model keeps producing force out to 90° AoA, both
          // resisting skid and feeding a yaw torque about the foil's lever.
          const STALL = (12 * Math.PI) / 180;
          const CL_ALPHA = 3.0;
          let CL: number;
          if (alphaAbs < STALL) {
            CL = CL_ALPHA * Math.sin(alpha);
          } else {
            const CL_stall = CL_ALPHA * Math.sin(STALL);
            const k = CL_stall / Math.sin(2 * STALL);
            CL = sgn * k * Math.sin(2 * alphaAbs);
          }
          const RHO = 1000;
          const q = 0.5 * RHO * vmag * vmag;
          // Lift acts perpendicular to local flow, towards the side that
          // opposes vside. In body frame the inward normal is
          //   n = (sinα, -cosα)   then F_lift = q·A·|CL|·n_signed.
          // Equivalent decomposition relative to (vfwd, vside):
          const Lmag = q * latArea * CL; // signed (sign comes from CL)
          // Direction in body frame: lift opposes local side-slip. For
          // positive leeway (vside > 0), the foil side force points to
          // port (body -Z), not further to starboard.
          const fX = vside / vmag;
          const fZ = -vfwd / vmag;
          liftSurge = Lmag * fX;
          liftSway = Lmag * fZ;
          // Induced drag along the flow (opposes velocity). Past stall add
          // a flat-plate normal-drag term ~2·sin²(α) so a stalled keel /
          // centerboard still strongly opposes lateral skid.
          const CDi =
            alphaAbs < STALL
              ? 0.05 + 0.5 * CL * CL
              : 0.05 + 2.0 * Math.sin(alphaAbs) * Math.sin(alphaAbs);
          const Dmag = q * latArea * CDi;
          liftSurge += -Dmag * (vfwd / vmag);
          liftSway += -Dmag * (vside / vmag);
          // Torque about CG = r × F  (here r = (ox, 0) in body XZ plane).
          // Yaw torque sign convention matches `T` below (positive yaws bow
          // toward +Z = starboard).
          liftYaw = ox * liftSway;
          this.leeway = alpha;
          this.lateralLift = Math.abs(Lmag);
        } else {
          this.leeway = 0;
          this.lateralLift = 0;
        }
      } else {
        this.leeway = 0;
        this.lateralLift = 0;
      }
    }

    // Net body-frame forces.
    // Project accumulated external world force into body frame.
    const extSurge = this.extForceWX * fwdX + this.extForceWZ * fwdZ;
    const extSway = this.extForceWX * rgtX + this.extForceWZ * rgtZ;
    const Fsurge =
      engineFx + windSurge + dragSurge + liftSurge + rudderSurge + extSurge;
    const Fsway =
      engineFz + windSway + dragSway + bowF + liftSway + rudderSway + extSway;
    // Yaw torque. Distributed `lateralPanels` already provide nonlinear
    // yaw damping (each panel sees `sway + yawRate*ox` and contributes
    // `ox * F_side` to `liftYaw`), so we only keep the linear `dragYaw`
    // baseline here. For procedural fallbacks (no panels) the linear
    // term plus rudder/wind torque are usually enough at design speeds.
    const T =
      engineYaw +
      rudderYaw -
      p.dragYaw * this.yawRate +
      bowF * bowLever +
      windYawTorque +
      liftYaw +
      this.extYawTorque;
    // Clear external accumulators — mooring/fender code re-applies them next frame.
    this.extForceWX = 0;
    this.extForceWZ = 0;
    this.extYawTorque = 0;

    // Semi-implicit Euler.
    this.surge += (Fsurge / p.mass) * dt;
    this.sway += (Fsway / p.mass) * dt;
    this.yawRate += (T / p.Iyy) * dt;

    // Integrate position in world frame from updated body velocities.
    const newWorldVx = this.surge * fwdX + this.sway * rgtX;
    const newWorldVz = this.surge * fwdZ + this.sway * rgtZ;
    this.posX += newWorldVx * dt;
    this.posZ += newWorldVz * dt;
    this.heading += this.yawRate * dt;

    // -------------------------------------------------------------------
    // Buoyancy: 6 hull sample points → vertical heave + pitch + roll.
    // Sample points sit on the keel line (body Y = -KEEL_DEPTH below the
    // hull centreline) so the equilibrium posY puts the hull mostly out of
    // the water, with a realistic draught.
    // -------------------------------------------------------------------
    const L = p.length;
    /** Offset of sample points below hull centreline (m). */
    const KEEL_DEPTH = p.keelDepth;
    // Body-frame offsets (ox = forward, oz = right). All on the keel.
    const SAMPLES =
      p.keelSamplePoints ?? defaultKeelSamplePoints(p.length, p.beam);
    const N = SAMPLES.length;
    const G_ACC = 9.81;
    /** Target equilibrium submersion of the keel below the surface. */
    const D_EQ = p.draftEq;
    // Per-point spring constant: N · K · d_eq = m · g  →  posY equilibrium
    // sits KEEL_DEPTH − D_EQ above water, i.e. ≈ 0.6 m for current numbers.
    const K_BUOY = (p.mass * G_ACC) / (N * D_EQ);
    const C_HEAVE = 12000;
    const C_PITCH = 90000;
    const C_ROLL = 9500;
    const I_PITCH = (p.mass * L * L) / 12;
    const I_ROLL = (p.mass * p.beam * p.beam) / 12;

    const sinP = Math.sin(this.pitch);
    const cosP = Math.cos(this.pitch);
    const sinR = Math.sin(this.roll);

    let Fy = -p.mass * G_ACC;
    let Tp = 0; // pitch torque (about body +Z)
    let Tr = 0; // roll torque (about body +X)

    for (const [ox, oz] of SAMPLES) {
      // World XZ via yaw-only mapping (pitch/roll tilt of offsets is
      // negligible at the angles we ever reach).
      const wx = this.posX + ox * fwdX + oz * rgtX;
      const wz = this.posZ + ox * fwdZ + oz * rgtZ;
      // World Y of the keel point. Includes:
      //   posY                              — hull centreline
      //   −KEEL_DEPTH                       — body offset of the sample
      //   ox · sinP                         — pitch tilt at forward offset
      //   oz · (−cosP · sinR)               — roll tilt at lateral offset
      const pointY = this.posY - KEEL_DEPTH + ox * sinP + oz * (-cosP * sinR);
      const surfY = waterLevel + water.sampleHeight(wx, wz);
      const d = surfY - pointY;
      if (d > 0) {
        const F = K_BUOY * d;
        Fy += F;
        Tp += F * ox; // bow submerged → +pitch (bow up)
        Tr -= F * oz; // stbd submerged → -roll (rights itself)
      }
    }

    // Damping.
    Fy -= C_HEAVE * this.vy;
    Tp -= C_PITCH * this.pitchRate;
    Tr -= C_ROLL * this.rollRate;

    // Semi-implicit Euler.
    this.vy += (Fy / p.mass) * dt;
    this.posY += this.vy * dt;
    this.pitchRate += (Tp / I_PITCH) * dt;
    this.pitch += this.pitchRate * dt;
    this.rollRate += (Tr / I_ROLL) * dt;
    this.roll += this.rollRate * dt;

    // Numerical safety clamps.
    if (this.pitch > 0.4) {
      this.pitch = 0.4;
      this.pitchRate = 0;
    }
    if (this.pitch < -0.4) {
      this.pitch = -0.4;
      this.pitchRate = 0;
    }
    if (this.roll > 0.5) {
      this.roll = 0.5;
      this.rollRate = 0;
    }
    if (this.roll < -0.5) {
      this.roll = -0.5;
      this.rollRate = 0;
    }
  }
}
